import { MatterLiensTable } from "@/components/features/matter/liens/MatterLiensTable";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { LienIcon } from "@/lib/utils/iconUtils";
import { calculateLienTotals } from "@/lib/utils/matters/lien-utils";
import { ErrorBoundary } from "@sentry/react";
import { useEffect, useMemo, useRef } from "react";
import { MatterTreatmentBillsDataTable } from "../medical-treatments/MatterTreatmentBillsCard/MatterTreatmentBillsDataTable";

interface MatterLiensProps {
  matter: Matter;
  searchLienId?: string;
}

function MatterLiensDangerous({ matter, searchLienId }: MatterLiensProps) {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailLiens,
  );
  const processedSearches = useRef<Set<string>>(new Set());
  const { openLienModal } = useGlobalModal();
  const { handleLienSearch } = useHandleSearchParams();
  useEffect(() => {
    if (!searchLienId) return;

    const searchKey = `lien-${searchLienId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleLienSearch(matter, searchLienId);
  }, [searchLienId, matter, handleLienSearch]);

  const { totalLiens, unpaidLiensAmount, unpaidLiensCount } =
    calculateLienTotals(matter.liens);

  const medicalTreatmentBillsWithLiens = useMedicalTreatmentBillViewModel(
    matter.id,
  ).filter((bill) => bill.lien);

  const pageHighlightCards = useMemo(() => {
    return [
      {
        title: "Liens",
        value: matter.liens.length,
      },
      {
        title: "Unpaid liens",
        value: unpaidLiensCount,
      },
      {
        title: "Sum of liens",
        value: totalLiens,
        isCurrency: true,
      },
      {
        title: "Unpaid liens amount",
        value: unpaidLiensAmount,
        isCurrency: true,
      },
    ];
  }, [matter.liens, totalLiens, unpaidLiensAmount, unpaidLiensCount]);

  return (
    <div className="flex flex-col gap-y-4 p-6">
      <Text as="h1" variant="title">
        Liens
      </Text>
      {hasViewPermission && (
        <PageHighlightCards items={pageHighlightCards} variant="summary" />
      )}
      {hasActionPermission(
        ACTION_PERMISSION_KEYS.matterLien,
        ACTION_PERMISSION_VALUES.create,
      ) && (
        <div className="flex w-full flex-1 grow flex-wrap items-center justify-end gap-x-2">
          <Button
            onClick={() => {
              openLienModal({
                matterId: matter.id,
                lienId: undefined,
                editing: false,
                initialTab: "details",
              });
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <LienIcon className="h-4 w-4" />
            Create lien
          </Button>
        </div>
      )}

      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view liens.
            </Text>
          </CardHeader>
        </Card>
      )}
      {hasViewPermission && (
        <>
          {matter.liens.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1 text-center">
                <Text
                  variant="body"
                  className="text-muted-foreground font-semibold"
                >
                  You haven&apos;t added a lien yet.
                </Text>
                <Text variant="subHeading">
                  Create your first by clicking &apos;Create lien&apos;
                </Text>
              </CardHeader>
            </Card>
          ) : (
            <Card className="w-full border-none">
              <CardHeader className="space-y-1">
                <Text
                  variant="body"
                  color="primary"
                  className="text-lg font-semibold"
                >
                  Liens
                </Text>
              </CardHeader>
              <CardContent className="p-6">
                <Card className="border-none">
                  <CardContent className="bg-accent pt-4">
                    <div className="grid grid-cols-1 gap-4">
                      <MatterLiensTable matter={matter} />
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          )}

          {medicalTreatmentBillsWithLiens.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1 text-center">
                <Text
                  variant="body"
                  className="text-muted-foreground font-semibold"
                >
                  There are currently no medical bills with liens.
                </Text>
              </CardHeader>
            </Card>
          ) : (
            <Card className="w-full border-none">
              <CardHeader className="space-y-1">
                <Text
                  variant="body"
                  color="primary"
                  className="text-lg font-semibold"
                >
                  Medical bills with liens
                </Text>
              </CardHeader>

              <CardContent className="p-6">
                <Card className="border-none">
                  <CardContent className="bg-accent pt-4">
                    <div className="grid grid-cols-1 gap-4">
                      <MatterTreatmentBillsDataTable
                        matter={matter}
                        bills={medicalTreatmentBillsWithLiens}
                      />
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default function MatterLiens(props: MatterLiensProps) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <MatterLiensDangerous {...props} />
    </ErrorBoundary>
  );
}
